import { useCallback, useEffect, useRef, useState } from 'react';
import type { OutputState } from '../../shared/api/types';

export type Playback = NonNullable<OutputState['playback']>;

/**
 * Cómo se reproduce un video en una salida o en el monitor En vivo de la consola: obedece el último
 * comando de reproducción (pausa, posición, volumen) y se mantiene parejo con las demás salidas.
 * `muted`: el escenario, OBS y la consola van sin sonido; la congregación lleva el sonido.
 */
export interface PlayerSync {
  playback: Playback | null | undefined;
  serverTime: string;
  muted: boolean;
  /** La consola lee de aquí el tiempo y la duración para su barra. */
  onInfo?: (info: { current: number; duration: number }) => void;
}

/** Posición en la que debe ir el video ahora (con la hora del servidor). */
export function expectedPosition(p: Playback, serverTime: string, offsetRef: { current: number | null }): number {
  if (offsetRef.current === null) offsetRef.current = new Date(serverTime).getTime() - Date.now();
  if (!p.playing || !p.at) return p.position;
  return p.position + Math.max(0, (Date.now() + offsetRef.current - new Date(p.at).getTime()) / 1000);
}

/** Video de la biblioteca o de la computadora, sin controles, sincronizado con el comando. */
export function SyncedVideo({ src, className, sync }: { src: string; className: string; sync: PlayerSync }) {
  const ref = useRef<HTMLVideoElement>(null);
  const offset = useRef<number | null>(null);
  const p = sync.playback;
  useEffect(() => {
    offset.current = new Date(sync.serverTime).getTime() - Date.now();
  }, [sync.serverTime]);
  useEffect(() => {
    const v = ref.current;
    if (!v || !p) return;
    const apply = () => {
      const want = expectedPosition(p, sync.serverTime, offset);
      if (Number.isFinite(v.duration) && want >= v.duration) {
        if (!v.paused) v.pause();
      } else if (Math.abs(v.currentTime - want) > 0.6) v.currentTime = want;
      if (p.playing && v.paused && !(Number.isFinite(v.duration) && want >= v.duration)) void v.play().catch(() => undefined);
      if (!p.playing && !v.paused) v.pause();
      v.muted = sync.muted || p.muted;
      v.volume = Math.min(1, Math.max(0, p.volume / 100));
    };
    apply();
    const t = setInterval(apply, 1000);
    return () => clearInterval(t);
    // Se vuelve a aplicar con cada comando (su secuencia) o al cambiar de video.
  }, [p?.seq, src, sync.muted]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <video
      ref={ref}
      className={className}
      src={src}
      autoPlay={p ? p.playing : true}
      muted={sync.muted || Boolean(p?.muted)}
      playsInline
      disablePictureInPicture
      onTimeUpdate={(e) => sync.onInfo?.({ current: e.currentTarget.currentTime, duration: e.currentTarget.duration })}
      onLoadedMetadata={(e) => sync.onInfo?.({ current: e.currentTarget.currentTime, duration: e.currentTarget.duration })}
    />
  );
}

/** Reproductor de YouTube para las salidas: sin controles, título, logo ni sugerencias. */
export const youtubePlayerUrl = (id: string, muted: boolean) =>
  `https://www.youtube.com/embed/${id}?autoplay=1&controls=0&disablekb=1&fs=0&rel=0&iv_load_policy=3&playsinline=1&modestbranding=1&enablejsapi=1&mute=${muted ? 1 : 0}&origin=${encodeURIComponent(typeof window !== 'undefined' ? window.location.origin : '')}`;

/**
 * YouTube sincronizado con la IFrame Player API (postMessage: playVideo, pauseVideo, seekTo,
 * setVolume, mute). Una capa transparente encima evita que el ratón haga aparecer la interfaz y el
 * iframe va un poco más grande dentro de un marco que recorta el título y la marca de las orillas.
 */
export function SyncedYouTube({ id, title, sync }: { id: string; title: string; sync: PlayerSync }) {
  const frame = useRef<HTMLIFrameElement>(null);
  const offset = useRef<number | null>(null);
  const info = useRef<{ current: number; duration: number; state: number }>({ current: 0, duration: 0, state: -1 });
  const p = sync.playback;
  const onInfo = useRef(sync.onInfo);
  onInfo.current = sync.onInfo;

  const command = (func: string, args: unknown[] = []) => frame.current?.contentWindow?.postMessage(JSON.stringify({ event: 'command', func, args }), '*');

  // Lo que cuenta el reproductor: tiempo, duración y si está reproduciendo.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.source !== frame.current?.contentWindow || typeof e.data !== 'string') return;
      try {
        const data = JSON.parse(e.data) as { event?: string; info?: { currentTime?: number; duration?: number; playerState?: number } };
        if (data.event === 'infoDelivery' && data.info) {
          if (typeof data.info.currentTime === 'number') info.current.current = data.info.currentTime;
          if (typeof data.info.duration === 'number') info.current.duration = data.info.duration;
          if (typeof data.info.playerState === 'number') info.current.state = data.info.playerState;
          onInfo.current?.({ current: info.current.current, duration: info.current.duration });
        }
      } catch {
        // Mensajes que no son del reproductor.
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    offset.current = new Date(sync.serverTime).getTime() - Date.now();
  }, [sync.serverTime]);

  useEffect(() => {
    if (!p) return;
    const apply = () => {
      const want = expectedPosition(p, sync.serverTime, offset);
      if (Math.abs(info.current.current - want) > 1.5 && (info.current.duration === 0 || want < info.current.duration)) command('seekTo', [want, true]);
      if (p.playing && info.current.state !== 1) command('playVideo');
      if (!p.playing && info.current.state === 1) command('pauseVideo');
      command('setVolume', [p.volume]);
      command(sync.muted || p.muted ? 'mute' : 'unMute');
    };
    apply();
    const t = setInterval(apply, 1000);
    return () => clearInterval(t);
  }, [p?.seq, id, sync.muted]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="slide__media yt-frame">
      <iframe
        key={id}
        ref={frame}
        className="yt-frame__iframe"
        src={youtubePlayerUrl(id, sync.muted)}
        title={title}
        allow="autoplay; encrypted-media"
        referrerPolicy="strict-origin-when-cross-origin"
        onLoad={() => frame.current?.contentWindow?.postMessage(JSON.stringify({ event: 'listening', id: 1, channel: 'widget' }), '*')}
      />
      <div className="yt-frame__shield" aria-hidden="true" />
    </div>
  );
}

/** ¿El elemento al aire es un video que se puede pausar o adelantar? */
export const isVideoSlide = (slide: { kind: string; youtube?: string | null; resource?: { kind: string } | null; localFile?: { kind: string } | null } | null | undefined) =>
  Boolean(slide && slide.kind === 'media' && (slide.youtube || slide.resource?.kind === 'video' || slide.localFile?.kind === 'video'));

const clock = (s: number) => {
  if (!Number.isFinite(s) || s < 0) return '0:00';
  const t = Math.floor(s);
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const sec = String(t % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
};

/**
 * Controles del video en vivo (consola y celular): pausa/reproducir, −10 s, +10 s, una barra para
 * ir a cualquier punto con el tiempo actual/total, y volumen o silencio. Mandan un comando de
 * reproducción que todas las salidas obedecen.
 */
export function PlaybackBar({
  playback,
  serverTime,
  info,
  send,
  compact = false,
}: {
  playback: Playback | null | undefined;
  serverTime: string;
  info: { current: number; duration: number } | null;
  send: (c: { action: 'playback'; playing?: boolean; seek?: number; delta?: number; volume?: number; muted?: boolean }) => void;
  compact?: boolean;
}) {
  const offset = useRef<number | null>(null);
  const [, tick] = useReducerTick();
  useEffect(() => {
    offset.current = new Date(serverTime).getTime() - Date.now();
  }, [serverTime]);
  useEffect(() => {
    const t = setInterval(tick, 500);
    return () => clearInterval(t);
  }, [tick]);
  if (!playback) return null;
  const duration = info?.duration && Number.isFinite(info.duration) ? info.duration : 0;
  const now = Math.min(duration || Infinity, expectedPosition(playback, serverTime, offset));
  return (
    <div className={`playbar${compact ? ' playbar--compact' : ''}`} role="group" aria-label="Video en vivo">
      <button type="button" className="playbar__btn" aria-label="Atrasar 10 segundos" onClick={() => send({ action: 'playback', delta: -10 })}>
        −10 s
      </button>
      <button type="button" className="playbar__btn playbar__btn--main" aria-label={playback.playing ? 'Pausar' : 'Reproducir'} onClick={() => send({ action: 'playback', playing: !playback.playing })}>
        {playback.playing ? '❚❚' : '▶'}
      </button>
      <button type="button" className="playbar__btn" aria-label="Adelantar 10 segundos" onClick={() => send({ action: 'playback', delta: 10 })}>
        +10 s
      </button>
      {!compact ? (
        <>
          <span className="playbar__time">{clock(now)}</span>
          <input
            type="range"
            className="playbar__seek"
            aria-label="Ir a un punto del video"
            min={0}
            max={Math.max(1, Math.round(duration))}
            step={1}
            value={Math.round(now)}
            disabled={!duration}
            onChange={(e) => send({ action: 'playback', seek: Number(e.target.value) })}
          />
          <span className="playbar__time">{duration ? clock(duration) : '—'}</span>
          <button type="button" className="playbar__btn" aria-label={playback.muted ? 'Quitar silencio' : 'Silenciar'} onClick={() => send({ action: 'playback', muted: !playback.muted })}>
            {playback.muted ? 'Sin sonido' : 'Sonido'}
          </button>
          <input type="range" className="playbar__vol" aria-label="Volumen" min={0} max={100} step={5} value={playback.volume} onChange={(e) => send({ action: 'playback', volume: Number(e.target.value) })} />
        </>
      ) : null}
    </div>
  );
}

function useReducerTick(): [number, () => void] {
  const [n, set] = useState(0);
  return [n, useCallback(() => set((x) => x + 1), [])];
}
